import { z } from 'zod';
const IsoDate = z.string().date('valid YYYY-MM-DD date required');
export const IncidentStatus = z.enum(['open', 'acknowledged', 'investigating', 'resolved', 'closed']);
export const IncidentSeverity = z.enum(['low', 'medium', 'high', 'critical']);
/**
 * A fault report. Always against a model; optionally pinned to the unit and
 * installation where it was observed. A recall may be raised from one (recallId).
 */
export const IncidentSubmission = z
    .object({
    modelId: z.string().optional(),
    unitId: z.string().optional(),
    installationId: z.string().optional(),
    severity: IncidentSeverity,
    title: z.string().min(3).max(200),
    description: z.string().min(10).max(5000),
    occurredOn: IsoDate.optional(),
    evidenceRef: z.string().max(500).optional(),
})
    .strict()
    .refine((s) => s.modelId || s.unitId || s.installationId, { message: 'a model, unit or installation is required' });
export const IncidentEvent = z.object({
    /** Surrogate id of the event. @metadata technical @source incidents.incident_events.id @transform bigint to number */
    id: z.number().int(),
    /** Kind of event: raised, note or status. @metadata business @source incidents.incident_events.kind */
    kind: z.enum(['raised', 'note', 'status']),
    /** Status before a status change. @metadata business @source incidents.incident_events.from_status */
    fromStatus: IncidentStatus.nullable(),
    /** Status after a status change. @metadata business @source incidents.incident_events.to_status */
    toStatus: IncidentStatus.nullable(),
    /** Note recorded with the event. @metadata business @source incidents.incident_events.note */
    note: z.string().nullable(),
    /** User who recorded the event. @metadata technical @source incidents.incident_events.actor_id */
    actorId: z.string().nullable(),
    /** Display name of the user who recorded the event. @metadata technical @source iam.users.display_name via incidents.incident_events.actor_id @transform left join */
    actorName: z.string().nullable(),
    /** Participant the user acted for. @metadata technical @source incidents.incident_events.actor_participant_id */
    actorParticipantId: z.string().nullable(),
    /** When the event was recorded. @metadata technical @source incidents.incident_events.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
});
export const IncidentSummary = z.object({
    /** Surrogate id of the incident. @metadata technical @source incidents.incidents.id */
    id: z.string(),
    /** Incident status: open, acknowledged, investigating, resolved or closed. @metadata business @source incidents.incidents.status */
    status: IncidentStatus,
    /** Severity: low, medium, high or critical. @metadata business @source incidents.incidents.severity */
    severity: IncidentSeverity,
    /** Short title of the fault report. @metadata business @source incidents.incidents.title */
    title: z.string(),
    /** Id of the model the incident is against. @metadata technical @source incidents.incidents.model_id */
    modelId: z.string(),
    /** Model number of the model. @metadata business @source register.device_models.model_number via incidents.incidents.model_id @transform joined from the model */
    modelNumber: z.string(),
    /** Participant id of the model's manufacturer. @metadata technical @source register.device_models.manufacturer_id via incidents.incidents.model_id @transform joined from the model */
    manufacturerId: z.string(),
    /** Legal name of the model's manufacturer. @metadata business @source register.participants.legal_name via register.device_models.manufacturer_id @transform joined from the manufacturer's participant record */
    manufacturerName: z.string(),
    /** Id of the unit where the fault was observed, if pinned. @metadata technical @source incidents.incidents.unit_id */
    unitId: z.string().nullable(),
    /** Serial number of that unit. @metadata business @source register.device_units.serial via incidents.incidents.unit_id @transform left join; null when no unit */
    unitSerial: z.string().nullable(),
    /** Id of the installation where the fault was observed, if pinned. @metadata technical @source incidents.incidents.installation_id */
    installationId: z.string().nullable(),
    /** NMI of that installation. @metadata business @source register.installations.nmi via incidents.incidents.installation_id @transform left join; null when no installation */
    installationNmi: z.string().nullable(),
    /** Participant that reported the incident; null when CER reported it. @metadata technical @source incidents.incidents.reported_by_participant_id */
    reportedByParticipantId: z.string().nullable(),
    /** Legal name of the reporting participant. @metadata business @source register.participants.legal_name via incidents.incidents.reported_by_participant_id @transform left join */
    reportedByParticipantName: z.string().nullable(),
    /** Date the fault occurred, if known. @metadata business @source incidents.incidents.occurred_on */
    occurredOn: z.string().nullable(),
    /** Id of the recall raised from the incident, if any. @metadata technical @source incidents.incidents.recall_id */
    recallId: z.string().nullable(),
    /** When the incident was reported. @metadata technical @source incidents.incidents.created_at @transform timestamptz as an ISO 8601 UTC string */
    createdAt: z.string(),
    /** When the incident was last changed. @metadata technical @source incidents.incidents.updated_at @transform timestamptz as an ISO 8601 UTC string */
    updatedAt: z.string(),
});
export const Incident = IncidentSummary.extend({
    /** Full description of the fault. @metadata business @source incidents.incidents.description */
    description: z.string(),
    /** Reference to supporting evidence. @metadata business @source incidents.incidents.evidence_ref */
    evidenceRef: z.string().nullable(),
    /** User who reported the incident. @metadata technical @source incidents.incidents.reported_by_user_id */
    reportedByUserId: z.string().nullable(),
    /** Display name of the reporting user. @metadata technical @source iam.users.display_name via incidents.incidents.reported_by_user_id @transform left join */
    reportedByUserName: z.string().nullable(),
    /** The incident's trail of raised, note and status events. @metadata business @source derived: incidents.incident_events rows for the incident */
    events: z.array(IncidentEvent),
});
export const IncidentList = z.object({ items: z.array(IncidentSummary) });
export const IncidentQuery = z.object({
    status: IncidentStatus.optional(),
    modelId: z.string().optional(),
    participantId: z.string().optional(),
    q: z.string().max(200).optional(),
});
export const IncidentNote = z.object({ note: z.string().min(1).max(5000) }).strict();
export const IncidentStatusChange = z
    .object({
    status: z.enum(['acknowledged', 'investigating', 'resolved', 'closed']),
    note: z.string().min(3).max(5000),
})
    .strict();
//# sourceMappingURL=incidents.js.map